// Pure logic for the role editor — a small mirror of the server's rules
// (ca-backend/config/capabilities.js normalizeGrants). The server ALWAYS normalises
// again on save, so this only has to make the screen behave: switching a capability
// on switches on what it needs, switching one off switches off what depends on it,
// and a scope can never be wider than the scope it depends on.

export function buildFlat(modules) {
  const flat = {};
  modules.forEach(m => {
    flat[m.access.key] = { ...m.access, module: m.key, isAccess: true, requires: [] };
    m.groups.forEach(g => g.caps.forEach(c => { flat[c.key] = { ...c, module: m.key }; }));
  });
  return flat;
}

export function normalize(grants, flat) {
  const g = {};
  Object.entries(grants || {}).forEach(([k, v]) => {
    const c = flat[k];
    if (!c || !v || c.adminOnly) return;
    g[k] = c.scoped ? (v === 'own' ? 'own' : 'all') : true;
  });
  let changed = true;
  while (changed) {
    changed = false;
    for (const k of Object.keys(g)) {
      const c = flat[k];
      const reqs = c.requires || [];
      if (!reqs.every(r => g[r])) { delete g[k]; changed = true; continue; }
      if (c.scoped && g[k] === 'all' && reqs.some(r => flat[r] && flat[r].scoped && g[r] === 'own')) { g[k] = 'own'; changed = true; }
    }
  }
  return g;
}

export function dependentsOf(flat, key) {
  const out = new Set();
  const walk = k => Object.values(flat).forEach(c => {
    if ((c.requires || []).includes(k) && !out.has(c.key)) { out.add(c.key); walk(c.key); }
  });
  walk(key);
  return [...out];
}

// Switch a capability ON (with an optional scope). Returns { grants, alsoOn: [labels] }.
export function enableCap(grants, flat, key, scope) {
  const next = { ...grants };
  const alsoOn = [];
  const visit = (k, top) => {
    const c = flat[k];
    (c.requires || []).forEach(r => visit(r, false));
    if (!next[k]) {
      next[k] = c.scoped ? 'all' : true;
      if (!top && !c.isAccess) alsoOn.push(c.label);
    }
  };
  visit(key, true);
  if (flat[key].scoped && scope) next[key] = scope;
  return { grants: normalize(next, flat), alsoOn };
}

// Switch a capability OFF, together with everything that depends on it.
export function disableCap(grants, flat, key) {
  const next = { ...grants };
  const alsoOff = [];
  delete next[key];
  dependentsOf(flat, key).forEach(k => { if (next[k]) { delete next[k]; if (!flat[k].isAccess) alsoOff.push(flat[k].label); } });
  return { grants: normalize(next, flat), alsoOff };
}

// Change a scoped capability's scope. Widening to "all" also widens the scoped
// capabilities it needs (you cannot edit ALL tasks if you can only view YOUR tasks).
export function setScope(grants, flat, key, scope) {
  const next = { ...grants, [key]: scope };
  if (scope === 'all') {
    const widen = k => (flat[k].requires || []).forEach(r => { if (flat[r].scoped && next[r]) { next[r] = 'all'; widen(r); } });
    widen(key);
  }
  return normalize(next, flat);
}

export const isViewKey = (key) => {
  const s = key.split('.').pop();
  return /(^|_)view$/.test(s) || key.startsWith('reports.') && s !== 'export';
};

// Counts for a module tile: { on, total, open }
export function moduleSummary(mod, grants) {
  let total = 0, on = 0;
  mod.groups.forEach(g => g.caps.forEach(c => { if (c.adminOnly) return; total++; if (grants[c.key]) on++; }));
  return { on, total, open: !!grants[mod.access.key] };
}

export function countAll(modules, grants) {
  let total = 0, on = 0;
  modules.forEach(m => { const s = moduleSummary(m, grants); total += s.total + 1; on += s.on + (s.open ? 1 : 0); });
  return { on, total };
}

// Quick presets for a module
export function presetGrants(mod, flat, grants, kind) {
  const keep = { ...grants };
  mod.groups.forEach(g => g.caps.forEach(c => { delete keep[c.key]; }));
  delete keep[mod.access.key];
  if (kind === 'none') return normalize(keep, flat);
  const add = {};
  add[mod.access.key] = true;
  mod.groups.forEach(g => g.caps.forEach(c => {
    if (c.adminOnly) return;
    if (kind === 'all' || (kind === 'view' && (isViewKey(c.key) || c.widget))) add[c.key] = c.scoped ? 'all' : true;
  }));
  return normalize({ ...keep, ...add }, flat);
}

export function sameGrants(a, b) {
  const ka = Object.keys(a || {}), kb = Object.keys(b || {});
  if (ka.length !== kb.length) return false;
  return ka.every(k => a[k] === b[k]);
}

// Human sentence for the change log
export function describeAudit(e, flat) {
  const d = e.detail || {};
  const label = k => (flat && flat[k] ? flat[k].label : k);
  switch (e.action) {
    case 'role.create': return `Created role “${d.name}”`;
    case 'role.update': return `Renamed role “${d.from}” to “${d.to}”`;
    case 'role.delete': return `Deleted role “${d.name}”${d.moved_users ? ` (${d.moved_users} user${d.moved_users === 1 ? '' : 's'} moved to ${d.moved_to})` : ''}`;
    case 'user.role': return `Changed a user’s role to “${d.role}”`;
    case 'role.capabilities': {
      const add = (d.added || []).length, rem = (d.removed || []).length;
      const parts = [];
      if (add) parts.push(`+${add}`);
      if (rem) parts.push(`−${rem}`);
      return `Edited “${d.name}” permissions (${parts.join(' ') || 'no change'})`;
    }
    default: return e.action;
  }
}
